/** The filter bar above the Service Desk queue. All state is owned by the page. */

import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { SdPriority, SdStatus } from '@ampersandbrands/ui';
import { ClearableSearchInput } from '@/components/ClearableSearchInput';
import { FilterLabel } from '@/components/FilterLabel';
import { SearchableSelect } from '@/components/SearchableSelect';
import { useCategoriesAll, useStoresList } from '@/lib/useServiceDesk';
import type { SdQueueFilters } from '@/lib/serviceDeskTypes';
import { CategoryPicker } from './CategoryPicker';
import { ALL_PRIORITIES, ALL_STATUSES, PRIORITY_LABELS, STATUS_LABELS } from './sdLabels';
import { SD_INPUT } from './sdStyles';

export function QueueFilters({
  filters,
  onChange,
  activeCount,
  collapsed,
  onToggleCollapsed,
}: {
  filters: SdQueueFilters;
  onChange: (next: SdQueueFilters) => void;
  /** How many filters are narrowing the list, shown on the toggle. */
  activeCount: number;
  /** Hides the dropdowns (the search box stays). Omit on a phone, where the grid already folds the filters away. */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}) {
  const categories = useCategoriesAll();
  const stores = useStoresList();

  // Search waits for a short pause in typing before it asks the api.
  const [text, setText] = useState(filters.q);
  useEffect(() => setText(filters.q), [filters.q]);
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (text !== filters.q) onChange({ ...filters, q: text });
    }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const foldable = onToggleCollapsed !== undefined;
  const hidden = foldable && collapsed;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {foldable && (
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-expanded={!collapsed}
            aria-controls="sd-filter-fields"
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-amp-line bg-amp-white px-2.5 py-1.5 text-[13px] font-semibold text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
          >
            {collapsed ? <ChevronRight className="h-3.5 w-3.5" aria-hidden /> : <ChevronDown className="h-3.5 w-3.5" aria-hidden />}
            Filters
            {activeCount > 0 && (
              <span className="rounded-pill bg-amp-olive-deep px-1.5 text-[11px] text-amp-cream">{activeCount}</span>
            )}
          </button>
        )}
        <div className="min-w-[200px] flex-1">
          <label htmlFor="sd-search" className="sr-only">
            Search
          </label>
          <ClearableSearchInput
            id="sd-search"
            value={text}
            onChange={(v) => {
              setText(v);
              if (v === '') onChange({ ...filters, q: '' });
            }}
            placeholder="Search ticket key, title, or words"
            className={SD_INPUT}
          />
        </div>
      </div>
      <div
        id="sd-filter-fields"
        className={`gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 ${hidden ? 'hidden' : 'grid'}`}
      >
        <div>
          <FilterLabel as="div">Status</FilterLabel>
          <SearchableSelect<SdStatus>
            options={ALL_STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] }))}
            selected={filters.status}
            onChange={(status) => onChange({ ...filters, status })}
            placeholder="Any status"
            ariaLabel="Status"
            className="w-full"
          />
        </div>
        <div>
          <FilterLabel as="div">Priority</FilterLabel>
          <SearchableSelect<SdPriority>
            options={ALL_PRIORITIES.map((p) => ({ value: p, label: PRIORITY_LABELS[p] }))}
            selected={filters.priority}
            onChange={(priority) => onChange({ ...filters, priority })}
            placeholder="Any priority"
            ariaLabel="Priority"
            className="w-full"
          />
        </div>
        <div>
          <FilterLabel htmlFor="sd-assignee">Assigned to</FilterLabel>
          <select
            id="sd-assignee"
            value={filters.assignee}
            onChange={(e) => onChange({ ...filters, assignee: e.target.value })}
            className={SD_INPUT}
          >
            <option value="">Anyone</option>
            <option value="me">Me</option>
            <option value="unassigned">Unassigned</option>
          </select>
        </div>
        <div>
          <FilterLabel htmlFor="sd-sla">Deadline</FilterLabel>
          <select
            id="sd-sla"
            value={filters.sla}
            onChange={(e) => onChange({ ...filters, sla: e.target.value as SdQueueFilters['sla'] })}
            className={SD_INPUT}
          >
            <option value="">Any</option>
            <option value="breached">Late</option>
            <option value="warning">Due soon</option>
          </select>
        </div>
        <div>
          <FilterLabel as="div">Category</FilterLabel>
          <CategoryPicker
            idPrefix="sd-filter-cat"
            tree={categories.data ?? []}
            value={filters.category_id}
            anyLabel="Any category"
            onChange={(id) => onChange({ ...filters, category_id: id })}
          />
        </div>
        <div>
          <FilterLabel as="div">Store</FilterLabel>
          <SearchableSelect
            multiSelect={false}
            options={(stores.data ?? []).map((s) => ({ value: s.store_no, label: `${s.store_no} ${s.name}` }))}
            selected={filters.store_no ? [filters.store_no] : []}
            onChange={(v) => onChange({ ...filters, store_no: v[0] ?? '' })}
            placeholder="Any store"
            ariaLabel="Store"
            className="w-full"
          />
        </div>
      </div>
    </div>
  );
}
